import React, { useState, useMemo, useEffect, useRef } from 'react'
import { Pagination } from '@equinor/eds-core-react'
import styled from 'styled-components'
import { Sensor } from '@/features/dashboard/models'
import { SensorCard } from '@/features/dashboard/components/sensorCards'
import { useNavigate } from 'react-router-dom'
import { useGetDevices } from '@/core/hooks-and-services/devicesService'
import { SortItem } from '@/core/models/types'
import useSortSensors from './useSortSensors'
import { SORT_OPTIONS } from '../dashboardTable/TableBar'
import { useAtom } from 'jotai'
import { pageIndexDashboardAtom } from '@/core/store/globalStore'
interface ISensorCards {
    filteredSensors: Sensor[]
    onRefetchSensors: () => void
    isFetchingSensors: boolean
    sortSpecification: {
        tag: SortItem
        installationDate: SortItem
        device: SortItem
        sensorStatus: SortItem
        batteryCapacity: SortItem
        signalStrength: SortItem
    }
    onEditSensor?: (sensor: Sensor) => void
    totalItems?: number
    serverPaginated?: boolean
    pageSize?: number
}

const SensorCards = React.memo((props: ISensorCards) => {
    const {
        filteredSensors,
        sortSpecification = {
            tag: { ...SORT_OPTIONS.tag, selected: null },
            installationDate: SORT_OPTIONS.installationDate,
            device: { ...SORT_OPTIONS.device, selected: null },
            sensorStatus: { ...SORT_OPTIONS.sensorStatus, selected: null },
            batteryCapacity: { ...SORT_OPTIONS.batteryCapacity, selected: null },
            signalStrength: { ...SORT_OPTIONS.signalStrength, selected: null },
        },
        onEditSensor,
        totalItems,
        serverPaginated = false,
        pageSize,
    } = props
    const navigate = useNavigate()
    const { data: devices } = useGetDevices()

    const [currentPageIndex, setCurrentPageIndex] = useAtom(pageIndexDashboardAtom)
    const [itemsPerPage, setItemsPerPage] = useState(15)
    const [showItemIndicator, setShowItemIndicator] = useState(true)
    const [windowWidth, setWindowWidth] = useState(window.innerWidth)
    const [windowHeight, setWindowHeight] = useState(window.innerHeight)

    const topRef = useRef<HTMLDivElement | null>(null)

    const calculateItemsPerPage = () => {
        const cardWidth = 414
        const cardHeight = 212
        const padding = 8
        const gap = 8
        const spaceAroundCardContainerWidth = 242
        const spaceAroundCardContainerHeight = 250

        const totalWidthCard = cardWidth + 2 * padding + gap
        const totalHeigthCard = cardHeight + 2 * padding + gap
        const sensorCardContainerWidth = windowWidth - spaceAroundCardContainerWidth + gap
        const sensorCardContainerHeight = windowHeight - spaceAroundCardContainerHeight + gap

        const itemsWidth = Math.floor(sensorCardContainerWidth / totalWidthCard)
        const itemsHeight = Math.floor(sensorCardContainerHeight / totalHeigthCard)

        const calculatedItemsPerPage = itemsHeight * itemsWidth ? itemsHeight * itemsWidth : itemsWidth

        return calculatedItemsPerPage
    }

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth)
            setWindowHeight(window.innerHeight)
        }

        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    useEffect(() => {
        if (pageSize !== undefined) {
            setItemsPerPage(pageSize)
            setShowItemIndicator(windowWidth > 1024)
            return
        }
        if (windowWidth <= 1024) {
            setItemsPerPage(10)
            setShowItemIndicator(false)
        } else {
            const calculatedItemsPerPage = calculateItemsPerPage()
            setItemsPerPage(calculatedItemsPerPage)
            setShowItemIndicator(true)
        }
    }, [windowWidth, windowHeight, pageSize])

    useEffect(() => {
        requestAnimationFrame(() => {
            topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        })
    }, [currentPageIndex, itemsPerPage])

    const sortedSensors = useSortSensors({
        filteredSensors: serverPaginated ? [] : filteredSensors,
        sortSpecification,
    })

    const paginatedData = useMemo(() => {
        if (serverPaginated) return filteredSensors
        const startIndex = (currentPageIndex - 1) * itemsPerPage
        return sortedSensors.slice(startIndex, startIndex + itemsPerPage)
    }, [sortedSensors, currentPageIndex, itemsPerPage, filteredSensors, serverPaginated])

    const handleCardClick = (deviceKey: string) => {
        const encodedDeviceKey = encodeURIComponent(deviceKey)
        navigate(`/portfolio/${encodedDeviceKey}`)
    }

    return (
        <>
            <div ref={topRef} style={{ position: 'absolute', top: -100, left: 0 }} />
            <SensorCardsContainer>
                {paginatedData.map((sensor) => {
                    if (!sensor.sensorModel) return null
                    const device = devices.find((device) => device.key === sensor.sensorModel)
                    if (!device || !device.key) return null

                    return (
                        <SensorCard
                            key={sensor.sensorDbKey}
                            imageSrc={device?.url ? device.url : '/sensor_images/default.png'}
                            sensor={sensor}
                            showDetailsButton={true}
                            toSensorPortfolio={() => device && device.key && handleCardClick(device.key)}
                            onEditSensor={onEditSensor}
                        />
                    )
                })}
            </SensorCardsContainer>
            <PaginationContainer>
                <Pagination
                    totalItems={totalItems ?? filteredSensors.length}
                    itemsPerPage={itemsPerPage}
                    defaultPage={currentPageIndex}
                    onChange={(e, page) => setCurrentPageIndex(page)}
                    withItemIndicator={showItemIndicator}
                    key={currentPageIndex}
                />
            </PaginationContainer>
        </>
    )
})

export default SensorCards

const SensorCardsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    overflow-y: hidden;
`

const PaginationContainer = styled.div`
    display: flex;
    margin: 16px 0;
    justify-content: center;

    @media (max-width: 1024px) {
        margin-top: 50px;
        overflow: hidden;
        margin-bottom: 180px;
        font-size: 10px;
    }
`
